import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchFeederScores } from '../services/feederScoreService';

const EMPTY_STATE = { key: '', data: {}, loading: false, error: '' };

/**
 * Sincroniza Feeder Score con la selección geográfica y el rango del dashboard.
 *
 * Evita solicitudes duplicadas y descarta respuestas de una selección anterior.
 * La carga se inicia automáticamente cuando el contexto está completo, pero se
 * expone también `reload` para las vistas que necesitan reintentar la consulta.
 *
 * @param {object} options - Contexto de la consulta.
 * @returns {{scores: object, loading: boolean, error: string, reload: Function}}
 */
export default function useFeederScores({
  allowed,
  level,
  region,
  area,
  center,
  days,
  server,
  recalcParams,
}) {
  const [state, setState] = useState(EMPTY_STATE);
  const activeRequest = useRef(null);
  const exactRegion = level === 'region' && recalcParams?.mode === 'standard' && Boolean(recalcParams?.exact);
  const key = JSON.stringify([server, region, area, center, level, days, exactRegion]);
  const selectionKey = useRef(key);
  selectionKey.current = key;

  const reload = useCallback(async () => {
    const scopeReady = level === 'region'
      || (level === 'area' && area)
      || (level === 'centro' && area && center);
    const ready = server && days?.startDate && days?.dayRange && region && scopeReady;
    if (!allowed || !ready || activeRequest.current === key || state.key === key) return;

    activeRequest.current = key;
    setState({ key, data: {}, loading: true, error: '' });
    try {
      const scores = await fetchFeederScores({
        level,
        region,
        area,
        center,
        startDate: days.startDate,
        dayRange: days.dayRange,
        server,
        exactRegion,
      });
      if (selectionKey.current === key) {
        setState({ key, data: scores || {}, loading: false, error: '' });
      }
    } catch (error) {
      if (selectionKey.current === key) {
        setState({
          key,
          data: {},
          loading: false,
          error: error.message || 'No se pudo cargar alimentación.',
        });
      }
    } finally {
      if (activeRequest.current === key) activeRequest.current = null;
    }
  }, [allowed, key, state.key, level, region, area, center, days, server, exactRegion]);

  useEffect(() => {
    reload();
  }, [reload]);

  const isCurrent = state.key === key;
  return {
    scores: isCurrent ? state.data : {},
    loading: isCurrent && state.loading,
    error: isCurrent ? state.error : '',
    reload,
  };
}
